/**
 * Inventario — control de entradas/salidas + Kardex.
 *
 * Tabs:
 *   - "Stock"   → snapshot por producto (stock actual, valor, alertas)
 *   - "Kardex"  → historial completo por producto seleccionado
 *   - "Ajustes" → registro manual (merma, devolución, conteo)
 *
 * Las ENTRADAS se generan al recibir órdenes de compra (/purchase-orders).
 * Las SALIDAS se generan al facturar pedidos (/invoices/from-order/{id}).
 * Esta página NO crea movimientos de entrada/salida — solo ajustes manuales.
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Boxes, AlertTriangle, TrendingUp, TrendingDown, Plus, Minus, FileText, ArrowDownToLine, ArrowUpFromLine, Pencil, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";

const TIPO_META = {
  entrada: { label: "Entrada", Icon: ArrowDownToLine, color: "text-emerald-700", bg: "bg-emerald-50", ring: "border-emerald-200" },
  salida:  { label: "Salida",  Icon: ArrowUpFromLine, color: "text-rose-700",    bg: "bg-rose-50",    ring: "border-rose-200" },
  ajuste:  { label: "Ajuste",  Icon: Pencil,           color: "text-amber-700",   bg: "bg-amber-50",   ring: "border-amber-200" },
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Inventory() {
  const [tab, setTab] = useState("stock");
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState({ rows: [], summary: { total_products: 0, total_value: 0, low_count: 0, negative_count: 0 } });
  const [search, setSearch] = useState("");

  // Kardex state
  const [kardexCode, setKardexCode] = useState("");
  const [kardex, setKardex] = useState(null);
  const [kardexLoading, setKardexLoading] = useState(false);

  // Adjust dialog
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjust, setAdjust] = useState({ codigo: "", cantidad: "", motivo: "" });
  const [adjustSign, setAdjustSign] = useState("plus"); // plus | minus

  const fetchStock = async () => {
    try {
      const { data } = await api.get("/inventory/stock");
      setStockData(data);
    } catch { toast.error("Error al cargar el inventario"); }
  };

  useEffect(() => {
    setLoading(true);
    fetchStock().finally(() => setLoading(false));
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return stockData.rows;
    return stockData.rows.filter(r =>
      (r.codigo || "").toLowerCase().includes(q) ||
      (r.descripcion || "").toLowerCase().includes(q) ||
      (r.proveedor || "").toLowerCase().includes(q),
    );
  }, [stockData.rows, search]);

  const openKardex = async (codigo) => {
    setTab("kardex");
    setKardexCode(codigo);
    setKardexLoading(true);
    try {
      const { data } = await api.get(`/inventory/kardex?codigo=${encodeURIComponent(codigo)}`);
      setKardex(data);
    } catch { toast.error("Error al cargar Kardex"); }
    finally { setKardexLoading(false); }
  };

  const submitAdjust = async (e) => {
    e?.preventDefault();
    const qty = parseFloat(adjust.cantidad);
    if (!adjust.codigo) { toast.error("Selecciona un producto"); return; }
    if (!qty || qty <= 0) { toast.error("La cantidad debe ser mayor a 0"); return; }
    const signed = adjustSign === "minus" ? -qty : qty;
    try {
      await api.post("/inventory/adjust", {
        codigo: adjust.codigo,
        cantidad: signed,
        motivo: adjust.motivo,
      });
      toast.success("Ajuste registrado");
      setAdjustOpen(false);
      setAdjust({ codigo: "", cantidad: "", motivo: "" });
      setAdjustSign("plus");
      fetchStock();
      // If the kardex was open for this product, refresh it.
      if (kardexCode === adjust.codigo) openKardex(adjust.codigo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar ajuste");
    }
  };

  const summary = stockData.summary || {};

  if (loading) return <PageLoader label="Cargando inventario…" testid="inventory-loader" />;

  return (
    <div data-testid="inventory-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Inventario
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Stock actual, Kardex (historial) y ajustes manuales. Las entradas vienen de Órdenes de Compra recibidas y las salidas, de Facturas.
          </p>
        </div>
        <Button onClick={() => setAdjustOpen(true)} className="btn-primary rounded-sm" data-testid="inventory-new-adjustment-button">
          <Pencil className="h-4 w-4 mr-2" />Ajuste manual
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Productos" value={summary.total_products || 0} Icon={Boxes} />
        <KpiCard label="Valor del inventario" value={formatMoney(summary.total_value || 0)} Icon={Wallet} accent />
        <KpiCard label="Stock bajo" value={summary.low_count || 0} Icon={AlertTriangle} warn={(summary.low_count || 0) > 0} />
        <KpiCard label="En negativo" value={summary.negative_count || 0} Icon={TrendingDown} danger={(summary.negative_count || 0) > 0} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[
          { key: "stock",  label: "Stock actual" },
          { key: "kardex", label: "Kardex" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[rgb(0,47,167)] text-[rgb(0,47,167)]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`inventory-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "stock" && (
        <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="inventory-stock-card">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por código, descripción o proveedor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-sm pl-9"
              data-testid="inventory-stock-search"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Código</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Descripción</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Stock</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Costo</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Valor</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Proveedor</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Kardex</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.codigo} className={`border-b border-slate-100 ${r.stock < 0 ? "bg-rose-50/40" : r.low_alert ? "bg-amber-50/40" : ""}`} data-testid={`inventory-row-${r.codigo}`}>
                    <td className="py-2 px-3 mono font-medium">{r.codigo}</td>
                    <td className="py-2 px-3">{r.descripcion}</td>
                    <td className={`py-2 px-3 text-right font-bold mono ${r.stock < 0 ? "text-rose-700" : r.low_alert ? "text-amber-700" : "text-slate-800"}`}>
                      {r.stock.toLocaleString("es-CR")}
                      {r.stock < 0 && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-rose-100 text-rose-800 border border-rose-200">NEG</span>}
                      {r.stock >= 0 && r.low_alert && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">BAJO</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600">{formatMoney(r.costo)}</td>
                    <td className="py-2 px-3 text-right font-medium">{formatMoney(r.valor)}</td>
                    <td className="py-2 px-3 text-slate-600">{r.proveedor || "-"}</td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openKardex(r.codigo)} className="rounded-sm h-7" data-testid={`inventory-open-kardex-${r.codigo}`}>
                        <FileText className="h-3 w-3 mr-1" />Ver
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400">Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "kardex" && (
        <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="inventory-kardex-card">
          <div className="flex flex-col md:flex-row gap-3 mb-3 items-start md:items-center">
            <Select value={kardexCode} onValueChange={openKardex}>
              <SelectTrigger className="rounded-sm md:w-80" data-testid="kardex-product-select">
                <SelectValue placeholder="Selecciona un producto…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {stockData.rows.map(r => (
                  <SelectItem key={r.codigo} value={r.codigo}>
                    <span className="mono text-xs mr-2">{r.codigo}</span>
                    {r.descripcion} <span className="text-slate-400">({r.stock})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {kardex && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-500">Stock actual:</span>
                <span className={`font-bold mono ${kardex.stock_actual < 0 ? "text-rose-700" : "text-slate-800"}`}>
                  {kardex.stock_actual?.toLocaleString("es-CR") ?? 0}
                </span>
                {kardex.descripcion && <span className="text-slate-500 truncate">· {kardex.descripcion}</span>}
              </div>
            )}
          </div>
          {kardexLoading ? (
            <p className="text-sm text-slate-500 py-6 text-center">Cargando…</p>
          ) : !kardex ? (
            <p className="text-sm text-slate-400 py-10 text-center">Elegí un producto para ver su historial.</p>
          ) : kardex.movimientos.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center" data-testid="kardex-table">Sin movimientos aún para este producto.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kardex-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Tipo</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Referencia</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Cant.</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Costo unit.</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Saldo</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {[...kardex.movimientos].reverse().map((m, i) => {
                    const meta = TIPO_META[m.tipo] || TIPO_META.ajuste;
                    const Icon = meta.Icon;
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(m.created_at)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${meta.bg} ${meta.color} ${meta.ring}`}>
                            <Icon className="h-3 w-3" />{meta.label}
                          </span>
                        </td>
                        <td className="py-2 px-3 mono text-xs text-slate-600">{m.referencia_numero || "-"}</td>
                        <td className={`py-2 px-3 text-right mono font-medium ${m.cantidad < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {m.cantidad > 0 ? "+" : ""}{m.cantidad.toLocaleString("es-CR")}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-600">{m.costo_unitario ? formatMoney(m.costo_unitario) : "-"}</td>
                        <td className="py-2 px-3 text-right font-bold mono">{m.balance?.toLocaleString("es-CR")}</td>
                        <td className="py-2 px-3 text-slate-600 text-xs">{m.nota || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Ajuste manual dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Ajuste manual de stock</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-3 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1">Producto *</label>
              <Select value={adjust.codigo} onValueChange={(v) => setAdjust({ ...adjust, codigo: v })}>
                <SelectTrigger className="rounded-sm" data-testid="adjust-product-select">
                  <SelectValue placeholder="Elegí un producto…" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {stockData.rows.map(r => (
                    <SelectItem key={r.codigo} value={r.codigo}>
                      <span className="mono text-xs mr-2">{r.codigo}</span>{r.descripcion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <div className="grid grid-cols-2 gap-1">
                  <button type="button" onClick={() => setAdjustSign("plus")}
                    className={`h-9 rounded-sm text-xs font-semibold border flex items-center justify-center gap-1 ${adjustSign === "plus" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid="adjust-plus">
                    <Plus className="h-3 w-3" />Suma
                  </button>
                  <button type="button" onClick={() => setAdjustSign("minus")}
                    className={`h-9 rounded-sm text-xs font-semibold border flex items-center justify-center gap-1 ${adjustSign === "minus" ? "bg-rose-600 text-white border-rose-600" : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid="adjust-minus">
                    <Minus className="h-3 w-3" />Resta
                  </button>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Cantidad *</label>
                <Input type="number" min="0" step="any" value={adjust.cantidad}
                  onChange={(e) => setAdjust({ ...adjust, cantidad: e.target.value })}
                  className="rounded-sm" data-testid="adjust-quantity-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Motivo</label>
              <Textarea rows={2} value={adjust.motivo}
                onChange={(e) => setAdjust({ ...adjust, motivo: e.target.value })}
                placeholder="Ej: Merma por vencimiento, conteo físico, devolución de cliente…"
                className="rounded-sm" data-testid="adjust-motivo-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAdjustOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm" data-testid="adjust-submit-button">Registrar ajuste</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, Icon, accent, warn, danger }) {
  const ring = danger ? "border-rose-200" : warn ? "border-amber-200" : "border-slate-200";
  const iconColor = danger ? "text-rose-600" : warn ? "text-amber-600" : "text-[rgb(0,47,167)]";
  return (
    <Card className={`bg-white border ${ring} rounded-sm p-4 flex items-center justify-between`}>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">{label}</p>
        <p className={`text-2xl font-black ${accent ? "text-[rgb(0,47,167)]" : "text-slate-900"}`}>{value}</p>
      </div>
      <Icon className={`h-6 w-6 ${iconColor}`} />
    </Card>
  );
}
